import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Stepper, Step, StepLabel, List, ListItemButton, ListItemText, Typography, Paper, Alert } from '@mui/material';
import CircularProgress from '@mui/joy/CircularProgress';
import { Map } from '../../map/Map';
import LoadAllMissionsFromDb from '../../../services/loadAllMissions';
import openShuttleMissionForApproval from '../../../services/openShuttleMissionForApproval';
import approveShuttleMission from '../../../services/approveShuttleMission';
import declineShuttleMission from '../../../services/declineShuttleMission';
// Added for rebuilding main path from skeleton points similar to CreateOrUpdateTourForm logic
// Removed path rebuilding (calcPathBetween2points, yaw calculations) per request
import useAuth from '../../../hooks/Auth/useAuth';

const steps = ['Select Shuttle', 'Preview', 'Approve'];

export default function ShuttleMissionDialog({
  open,
  onClose,
  relevantUrl,
  cartInfo,
  categoriesArray,
  categoriesIcons,
  boundaries,
  roads,
  nonStopArea,
  inSlider = false,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [loadingTours, setLoadingTours] = useState(false);
  const [previewPaths, setPreviewPaths] = useState([]); // all full paths returned from backend
  const [previewMissionData, setPreviewMissionData] = useState(null); // store mission_data for cyclic flag
  const [loadingFullPaths, setLoadingFullPaths] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [pendingMissionId, setPendingMissionId] = useState(null);
  // Rebuild logic removed; keep original server main path
  // Visibility state for each segment type (start, main, cyclic)
  const [segmentVisibility, setSegmentVisibility] = useState({ start: true, main: true, cyclic: true });
  // carts data for showing cart icons like SiteOverview
  const { allExistsCarts, cartsLocationsArr, cartsLocationObject } = useAuth();
  const relevantCartNumber = cartInfo?.cart_number ?? cartInfo?.cart_id ?? null;
  const [dialogSelectedCart, setDialogSelectedCart] = useState(relevantCartNumber);

  // Keep selected cart synced if dialog cart changes
  useEffect(() => {
    if (relevantCartNumber != null) setDialogSelectedCart(relevantCartNumber);
  }, [relevantCartNumber]);

  // Filter carts arrays/objects to only include the relevant cart
  const filteredCartsLocationsArr = useMemo(() => {
    if (!Array.isArray(cartsLocationsArr) || relevantCartNumber == null) return [];
    return cartsLocationsArr.filter(c => Number(c?.cart_number) === Number(relevantCartNumber));
  }, [cartsLocationsArr, relevantCartNumber]);

  const filteredAllExistsCarts = useMemo(() => {
    if (!Array.isArray(allExistsCarts) || relevantCartNumber == null) return [];
    return allExistsCarts.filter(c => Number(c?.cart_number) === Number(relevantCartNumber));
  }, [allExistsCarts, relevantCartNumber]);

  const filteredCartsLocationObject = useMemo(() => {
    if (!cartsLocationObject || relevantCartNumber == null) return {};
    const entry = cartsLocationObject[relevantCartNumber];
    return entry ? { [relevantCartNumber]: entry } : {};
  }, [cartsLocationObject, relevantCartNumber]);
  // Loader defined before effects that depend on it to avoid TDZ issues
  const loadTours = useCallback(async () => {
    if (!relevantUrl) return;
    try {
      setLoadingTours(true);
      const mission_type = 'MISSION_TYPE_SHUTTLE';
      const resp = await LoadAllMissionsFromDb(relevantUrl, mission_type);
      if (Array.isArray(resp.data)) setTours(resp.data);
    } catch (e) {
      setError('Failed to load shuttle missions');
    } finally {
      setLoadingTours(false);
    }
  }, [relevantUrl]);

  // Reset when opened/closed
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedTour(null);
      setPreviewPaths([]);
      setResultMessage('');
  // (rebuild state removed)
      loadTours();
    } else {
      setTours([]);
    }
  }, [open, loadTours]);

  // Convert backend error fields into a concise, user-friendly message
  const buildFriendlyApprovalError = useCallback((raw) => {
    if (!raw) return 'Failed to open mission for approval';
    const base = raw.error || raw.message || 'Path calculation failed';
    const code = raw.resultCode;
    const step = raw.step;
    const reason = raw.errorReason || raw.reason || '';

    const stepMap = {
      validation: 'Validation',
      goto_start: 'Cart goes to mission start',
      cyclic_return: 'Cyclic return'
    };

    const codeMap = {
      cart_not_found: 'Cart not found',
      'destination far from any roads': 'Destination too far from roads',
      cart_is_offline: 'Cart is offline',
      path_calc_error: 'Path calculation failed',
      invalid_mission: 'Invalid mission data'
    };

    // Clean up details
    let details = typeof reason === 'string' ? reason : '';
    if (typeof details === 'string') {
      details = details.replace('cart->start', 'cart → start');
    }

    // Try to extract cart locations
    let cartLine = '';
    try {
      const idx = details.indexOf('Cart locations:');
      if (idx !== -1) {
        const jsonPart = details.slice(idx + 'Cart locations:'.length).trim();
        // Remove trailing punctuation
        const clean = jsonPart.replace(/^[\s:]+/, '').replace(/\s+$/, '');
        // Attempt to parse JSON array like: [["100",[lon,lat,0]]]
        const arr = JSON.parse(clean);
        if (Array.isArray(arr) && arr.length > 0 && Array.isArray(arr[0])) {
          const id = Array.isArray(arr[0]) ? arr[0][0] : undefined;
          const coords = Array.isArray(arr[0]) ? arr[0][1] : undefined; // [lon, lat, alt]
          const lon = Array.isArray(coords) ? Number(coords[0]) : undefined;
          const lat = Array.isArray(coords) ? Number(coords[1]) : undefined;
          if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
            cartLine = `Cart ${id} at ${lat.toFixed(6)}, ${lon.toFixed(6)}`;
          }
        }
        // Trim details before the JSON to keep the sentence
        details = details.slice(0, idx).trim();
      }
    } catch(_) { /* ignore parse issues */ }

    const lines = [];
    // Title from code or base
    if (code && codeMap[code]) lines.push(codeMap[code]); else lines.push(base);
    if (step && stepMap[step]) lines.push(`Error section: ${stepMap[step]}`);
    if (details) lines.push(`Details: ${details}`);
    if (cartLine) lines.push(cartLine);

    return lines.join('\n');
  }, []);

  // Request backend to calculate ALL paths (cart->start, shuttle segments, return if cyclic)
  const requestFullPaths = useCallback(async () => {
  if (!selectedTour) return { ok: false };
    setLoadingFullPaths(true);
    setError('');
  setPreviewPaths([]);
  setPreviewMissionData(null);
    setPendingMissionId(null);
    try {
      // Construct payload per backend docs
      const missionPayload = {
        mission_type: 'MISSION_TYPE_SHUTTLE',
        cart_id: cartInfo?.cart_id ?? cartInfo?.cart_number, // required by backend to calculate cart->start path
        mission: {
          ...selectedTour.mission_json,
          mission_id: selectedTour.mission_json?.mission_id || selectedTour.mission_id
        }
      };
      console.log(missionPayload)
      const resp = await openShuttleMissionForApproval(relevantUrl, missionPayload);
      console.log(resp);
      /*
        Actual backend response sample:
        {
          success: true,
          message: 'Mission created and awaiting approval',
          data: {
            success: true,
            mission_id: '...',
            mission_data: { mission_paths_list: [...], cyclic: true, ... },
            paths_calculated: 3,
            status: 'pending_approval',
            requested_cart_number: 100
          }
        }
        We support both original expected (flat) and current nested (data) shapes.
      */
      const container = resp?.data?.mission_data ? resp.data : resp; // choose nested level if present
      const success = !!resp && resp !== 'error' && (resp.success || container.success);
      const missionData = container.mission_data;
      const missionId = container.mission_id || missionData?.mission_id;
      const paths = missionData?.mission_paths_list;
      if (success && Array.isArray(paths)) {
        setPreviewPaths(paths);
        setPreviewMissionData(missionData);
        setPendingMissionId(missionId);
  // (rebuild state removed)
        return { ok: true };
      } else {
        // Build user-friendly error message from server fields
        setError(buildFriendlyApprovalError(resp));
        return { ok: false };
      }
    } catch (e) {
      const resp = e?.response?.data || e?.response || e || {};
      setError(buildFriendlyApprovalError(resp));
      return { ok: false };
    } finally {
      setLoadingFullPaths(false);
    }
  }, [selectedTour, relevantUrl, cartInfo, buildFriendlyApprovalError]);

  // Helper: rebuild the shuttle main path from skeleton points & assign tasks
  // Removed effect that rebuilt main path; we now trust server-provided path structure

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!selectedTour) { setError('Select a shuttle mission first'); return; }
  const res = await requestFullPaths();
  if (!res?.ok) return; // stay on current step if failed and show error
    }
    setActiveStep(s => s + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) return;
    // If backing out from Preview step (1) without approving -> decline silently
    if (activeStep === 1 && pendingMissionId && !resultMessage) {
      declinePendingMissionSilently();
      // Reset preview-related state
      setPreviewPaths([]);
      setPreviewMissionData(null);
      setPendingMissionId(null);
    }
    setActiveStep(s => s - 1);
  };

  const handleApprove = async () => {
    if (!pendingMissionId) return;
    try {
      setSubmitting(true); setError('');
      const resp = await approveShuttleMission(relevantUrl, { mission_id: pendingMissionId });
      if (resp !== 'error' && resp.success) {
        setResultMessage('Mission approved & sent to cart');
      } else {
        setError(resp?.error || 'Approve failed');
      }
    } catch(e){ setError('Approve error'); } finally { setSubmitting(false); }
  };

  const handleDecline = async () => {
    if (!pendingMissionId) return;
    try {
      setSubmitting(true); setError('');
      const resp = await declineShuttleMission(relevantUrl, { mission_id: pendingMissionId });
      if (resp !== 'error' && resp.success) {
        setResultMessage('Mission declined');
      } else {
        setError(resp?.error || 'Decline failed');
      }
    } catch(e){ setError('Decline error'); } finally { setSubmitting(false); }
  };

  // Silent decline used for auto-cleanup (no UI messaging, swallow errors)
  const declinePendingMissionSilently = useCallback(async () => {
    if (!pendingMissionId) return;
    try {
      await declineShuttleMission(relevantUrl, { mission_id: pendingMissionId });
    } catch(_) { /* ignore */ }
  }, [pendingMissionId, relevantUrl]);

  const handleClose = async (_, reason) => {
    if (submitting) return;
    // If a mission is open for approval (pending) and not yet approved/declined -> decline on close
    // Applies on any close from step 1 or step 2 while no resultMessage
    if (pendingMissionId && !resultMessage) {
      await declinePendingMissionSilently();
    }
    // Propagate upwards
  setError('');
    onClose && onClose();
  };

  // Ensure pending mission is declined if component unmounts without decision (e.g., parent closes slider)
  useEffect(() => {
    return () => {
      if (pendingMissionId && !resultMessage) {
        // fire and forget
        declinePendingMissionSilently();
      }
    };
  }, [pendingMissionId, resultMessage, declinePendingMissionSilently]);

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: 400 }}>
          {loadingTours ? <CircularProgress size="md" /> : null}
          <List dense sx={{ flex: 1, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
            {tours.map(t => (
              <ListItemButton
                key={t.mission_id}
                selected={selectedTour?.mission_id === t.mission_id}
                onClick={() => setSelectedTour(t)}
              >
                <ListItemText primary={t.mission_json?.mission_name || t.mission_name || 'Unnamed'} secondary={t.mission_id} />
              </ListItemButton>
            ))}
            {tours.length === 0 && !loadingTours && <Typography sx={{ p:1 }} variant='body2'>No shuttle missions found.</Typography>}
          </List>
        </Box>
      );
    }
    if (activeStep === 1) {
      // Decoration: 3 colors only (start, main legs, cyclic return) reused from Pending component
      const START_PATH_COLOR = '#ff6b6b';
      const MAIN_PATH_COLOR = '#4ecdc4';
      const CYCLIC_RETURN_COLOR = '#555555';
      const cyclic = !!previewMissionData?.cyclic;
      const total = previewPaths.length;
      const decorated = previewPaths.map((p, idx) => {
        const isStart = p.path_type === 'PATH_TYPE_SHUTTLE_GOTO_START_POINT' || idx === 0;
        const isCyclicReturn = cyclic && idx === total - 1 && total > 2 && !isStart;
  // Unified short labels to match buttons
  const label = isStart ? 'Start' : (isCyclicReturn ? 'Cyclic' : 'Main');
        const color = isStart ? START_PATH_COLOR : (isCyclicReturn ? CYCLIC_RETURN_COLOR : MAIN_PATH_COLOR);
        const dashed = isStart || isCyclicReturn;
        const classification = isStart ? 'start' : (isCyclicReturn ? 'cyclic' : 'main');
        return { ...p, ui_meta: { color, dashed, label, classification }, _segmentIndex: idx };
      });
      // Determine main (shuttle) path index (first non-start, non-cyclic segment)
      const mainPathIndex = decorated.findIndex((seg, idx) => {
        const isStart = seg.path_type === 'PATH_TYPE_SHUTTLE_GOTO_START_POINT' || idx === 0;
        const isCyclicReturn = cyclic && idx === total -1 && total > 2 && !isStart;
        return !isStart && !isCyclicReturn; // main leg(s)
      });
  // Filter by visibility toggles
  const visibleDecorated = decorated.filter(p => segmentVisibility[p.ui_meta.classification]);
  const hasStart = decorated.some(p => p.ui_meta.classification==='start');
  const hasMain = decorated.some(p => p.ui_meta.classification==='main');
  const hasCyclic = decorated.some(p => p.ui_meta.classification==='cyclic');
  // Recompute main index relative to visible array so markers appear even when start hidden
  const visibleMainIndex = visibleDecorated.findIndex(p => p.ui_meta.classification === 'main');
      const toggleSegment = (key) => {
        setSegmentVisibility(prev => ({ ...prev, [key]: !prev[key] }));
      };
      return (
        <Box sx={{ height: 420, position:'relative' }}>
          {loadingFullPaths ? (
            <Box sx={{display:'flex',justifyContent:'center',alignItems:'center',height:'100%'}}><CircularProgress size="md"/></Box>
          ) : (
            <>
              <Map
                isShuttleComponent={true}
                paths={visibleDecorated}
                shuttleMainPathIndex={visibleMainIndex >=0 ? visibleMainIndex : undefined}
                categoriesArray={categoriesArray}
                categoriesObject={categoriesIcons}
                bounderiesList={boundaries}
                roadsList={roads}
                showBoundaries={true}
                showRoads={true}
                nonStopArea={nonStopArea}
                showStartEndMarkers={segmentVisibility.main}
                showEndMarker={false}
                pathWeight='8'
                taskActive={true} // allow tasks display on preview
                isPreview={true}
                showCarts={true}
                allExistsCarts={filteredAllExistsCarts}
                carsLocations={filteredCartsLocationsArr}
                cartsLocationObject={filteredCartsLocationObject}
                selectedCart={dialogSelectedCart}
                setSelectedCart={setDialogSelectedCart}
                disableCartPopup={true}
                cartToShowPath={[]}
                setCartToShowPath={() => {}}
                cartsPaths={{}}
              />
              <Box sx={{ position:'absolute', inset:0, pointerEvents:'none', zIndex: (theme)=> theme.zIndex.drawer + 10 }}>
                {(hasStart || hasMain || hasCyclic) && (
                  <Paper elevation={3} sx={{ pointerEvents:'auto', position:'absolute', bottom:8, left:8, p:1, maxWidth:260, background:'rgba(255,255,255,0.95)', zIndex:(theme)=> theme.zIndex.drawer + 11 }}>
                    <Typography variant='caption' sx={{ fontWeight:600, display:'block', mb:0.5 }}>Segments</Typography>
                    <Box sx={{ display:'flex', gap:0.5, flexWrap:'wrap' }}>
                      <Button size='small'
                        startIcon={<Box sx={{ width:18, height:6, borderRadius:1, background: START_PATH_COLOR, ...(true? { background:'repeating-linear-gradient(90deg,'+START_PATH_COLOR+','+START_PATH_COLOR+' 4px,#ffffff 4px,#ffffff 8px)'}:{} ) }} />}
                        variant={segmentVisibility.start? 'contained':'outlined'}
                        onClick={() => toggleSegment('start')}
                        disabled={!hasStart}
                        sx={{ textTransform:'none', pr:1.2 }}>Start</Button>
                      <Button size='small'
                        startIcon={<Box sx={{ width:18, height:6, borderRadius:1, background: MAIN_PATH_COLOR }} />}
                        variant={segmentVisibility.main? 'contained':'outlined'}
                        onClick={() => toggleSegment('main')}
                        disabled={!hasMain}
                        sx={{ textTransform:'none', pr:1.2 }}>Main</Button>
                      <Button size='small'
                        startIcon={<Box sx={{ width:18, height:6, borderRadius:1, background: CYCLIC_RETURN_COLOR, backgroundImage:'repeating-linear-gradient(90deg,'+CYCLIC_RETURN_COLOR+','+CYCLIC_RETURN_COLOR+' 4px,#ffffff 4px,#ffffff 8px)' }} />}
                        variant={segmentVisibility.cyclic? 'contained':'outlined'}
                        onClick={() => toggleSegment('cyclic')}
                        disabled={!hasCyclic}
                        sx={{ textTransform:'none', pr:1.2 }}>Cyclic</Button>
                    </Box>
                  </Paper>
                )}
              </Box>
            </>
          )}
        </Box>
      );
    }
    if (activeStep === 2) {
      return (
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          <Typography variant='subtitle1'>Mission: {selectedTour?.mission_json?.mission_name}</Typography>
          <Typography variant='body2'>Mission ID: {selectedTour?.mission_id}</Typography>
          <Typography variant='body2'>Cart: {cartInfo?.cart_number}</Typography>
          {resultMessage && <Typography color='success.main'>{resultMessage}</Typography>}
        </Box>
      );
    }
    return null;
  };

  if (inSlider) {
    // Render inside BottomSlider content (no Dialog)
    return (
      <Box sx={{ display:'flex', flexDirection:'column', height:'100%' }}>
        <Box sx={{ pb: 1 }}>
          <Typography variant='h6'>Assign Shuttle Mission</Typography>
        </Box>
        <Box sx={{ flex:1, minHeight:0, overflow:'auto' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb:2 }}>
            {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>
          {error && (
            <Alert severity='error' variant='outlined' sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              {error}
            </Alert>
          )}
          {activeStep === 0 && loadingFullPaths && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size="sm" />
              <Typography variant='body2'>Calculating paths…</Typography>
            </Box>
          )}
          {renderStepContent()}
        </Box>
        <Box sx={{ pt: 1, display:'flex', gap:1, justifyContent:'flex-end' }}>
          {!(activeStep === steps.length - 1 && !!resultMessage) && (
            <Button onClick={() => handleClose()} disabled={submitting}>Close</Button>
          )}
          {activeStep > 0 && activeStep < steps.length && !resultMessage && (
            <Button onClick={handleBack} disabled={submitting}>Back</Button>
          )}
          {activeStep < steps.length -1 && (
            <Button
              variant='contained'
              onClick={handleNext}
              disabled={submitting || (activeStep===0 && (!selectedTour || loadingFullPaths))}
              endIcon={activeStep===0 && loadingFullPaths ? <CircularProgress size="sm" /> : null}
            >
              Next
            </Button>
          )}
          {activeStep === steps.length -1 && !resultMessage && (
            <Button color='primary' variant='contained' onClick={handleApprove} disabled={submitting}>{submitting? 'Approving...' : 'Approve'}</Button>
          )}
          {activeStep === steps.length -1 && resultMessage && (
            <Button variant='contained' onClick={onClose}>Done</Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='lg'>
      <DialogTitle>Assign Shuttle Mission</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb:2 }}>
          {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
        {error && (
          <Alert severity='error' variant='outlined' sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
            {error}
          </Alert>
        )}
    {activeStep === 0 && loadingFullPaths && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <CircularProgress size="sm" />
            <Typography variant='body2'>Calculating paths…</Typography>
          </Box>
        )}
        {renderStepContent()}
      </DialogContent>
      <DialogActions>
        {!(activeStep === steps.length - 1 && !!resultMessage) && (
          <Button onClick={handleClose} disabled={submitting}>Close</Button>
        )}
        {activeStep > 0 && activeStep < steps.length && !resultMessage && (
          <Button onClick={handleBack} disabled={submitting}>Back</Button>
        )}
        {activeStep < steps.length -1 && (
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={submitting || (activeStep===0 && (!selectedTour || loadingFullPaths))}
            endIcon={activeStep===0 && loadingFullPaths ? <CircularProgress size="sm" /> : null}
          >
            Next
          </Button>
        )}
        {activeStep === steps.length -1 && !resultMessage && (
          <Button color='primary' variant='contained' onClick={handleApprove} disabled={submitting}>{submitting? 'Approving...' : 'Approve'}</Button>
        )}
        {activeStep === steps.length -1 && resultMessage && (
          <Button variant='contained' onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
